import { useEffect, useRef, useState } from "react";
import { Mic, MicOff } from "lucide-react";
import { toast } from "sonner";

/**
 * Compact voice dictation button using the browser's Web Speech API.
 * Appends recognized transcript to `value` and calls `onChange(newValue)`.
 * Silently hides if the browser doesn't support speech recognition.
 */
export function VoiceDictationButton({ value, onChange, lang = "it-IT", "data-testid": testid }) {
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    const Ctor = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
    setSupported(!!Ctor);
    return () => {
      try { recognitionRef.current?.stop(); } catch (err) {
        // recognizer already stopped or garbage-collected — safe to ignore
        console.debug("SpeechRecognition cleanup stop failed:", err);
      }
    };
  }, []);

  const toggle = () => {
    const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Ctor) { toast.error("Il tuo browser non supporta la dettatura vocale"); return; }
    if (listening) {
      try { recognitionRef.current?.stop(); } catch (err) {
        // stop() may throw if recognizer is already inactive — safe to ignore
        console.debug("SpeechRecognition stop failed:", err);
      }
      return;
    }
    const rec = new Ctor();
    rec.lang = lang;
    rec.interimResults = false;
    rec.continuous = false;
    rec.onstart = () => setListening(true);
    rec.onend = () => setListening(false);
    rec.onerror = (e) => {
      setListening(false);
      if (e.error === "not-allowed") toast.error("Permesso microfono negato");
      else if (e.error !== "aborted") toast.error(`Errore vocale: ${e.error}`);
    };
    rec.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((r) => r[0].transcript)
        .join(" ")
        .trim();
      if (!transcript) return;
      const separator = value && !value.endsWith(" ") && !value.endsWith("\n") ? " " : "";
      onChange((value || "") + separator + transcript);
    };
    recognitionRef.current = rec;
    try { rec.start(); } catch (e) { setListening(false); toast.error("Impossibile avviare il microfono"); }
  };

  if (!supported) return null;

  return (
    <button
      type="button"
      data-testid={testid || "voice-dictate-btn"}
      onClick={toggle}
      title={listening ? "Interrompi dettatura" : "Detta con la voce"}
      className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-sm border text-xs transition-colors ${
        listening
          ? "border-red-300 bg-red-50 text-red-700 animate-pulse"
          : "border-zinc-300 text-zinc-700 hover:bg-zinc-50"
      }`}
    >
      {listening ? <><MicOff className="h-3.5 w-3.5" /> Ascolto...</> : <><Mic className="h-3.5 w-3.5" /> Detta</>}
    </button>
  );
}
